import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import SettingsModal from "../modals/SettingsModal";
import { fetchSettings, saveSettings } from "./settingsUtils";

function Header({
  user,
  onLogout,
  logoSrc = "/mast_branding.png",
  centerControl = null,
  onSettingsChange,
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  const loadSettings = async () => {
    setSettingsError("");
    setSettingsLoading(true);
    try {
      const fetched = await fetchSettings(user?.access_token);
      setSettings(fetched);
      return fetched;
    } catch (err) {
      setSettingsError(err.message);
      return null;
    } finally {
      setSettingsLoading(false);
    }
  };

  const openSettings = async () => {
    setOpen(false);
    setShowSettings(true);
    await loadSettings();
  };

  // On login, force the settings modal open until the user has verified
  // (saved) their settings at least once.
  useEffect(() => {
    if (!user) return;
    (async () => {
      const fetched = await loadSettings();
      if (fetched && fetched.settings_verified === false) {
        setShowSettings(true);
      }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveSettings = async (updated) => {
    setSettingsSaving(true);
    setSettingsError("");
    try {
      const saved = await saveSettings(user?.access_token, updated);
      setSettings(saved);
      setShowSettings(false);
      if (onSettingsChange) onSettingsChange(saved);
    } catch (err) {
      setSettingsError(err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full">
      <header
        className="w-full flex items-center justify-between bg-white shadow"
        style={{ minHeight: "64px" }}
      >
        <div className="flex items-center px-4">
          <Link to="/">
            <img src={logoSrc} alt="JokeLab Mast" style={{ height: 48 }} />
          </Link>
        </div>
        <div className="flex-1 flex">{centerControl}</div>
        <div className="relative" ref={dropdownRef}>
          <button
            className="flex items-center gap-1 text-sm text-green-900 font-medium focus:outline-none px-2 py-1 rounded hover:bg-yellow-100"
            onClick={() => setOpen((v) => !v)}
          >
            {user?.profile?.preferred_username ||
              (user?.profile?.email
                ? user.profile.email.split("@")[0]
                : "User")}
            <FiChevronDown size={14} />
          </button>
          {open && (
            <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-10">
              <button
                className="block w-full text-left px-4 py-2 hover:bg-yellow-100 text-green-900"
                onClick={openSettings}
              >
                Settings
              </button>
              <Link
                to="/help"
                className="block w-full text-left px-4 py-2 hover:bg-yellow-100 text-green-900"
                onClick={() => setOpen(false)}
              >
                Help
              </Link>
              <div className="border-t border-gray-100" />
              <button
                className="block w-full text-left px-4 py-2 hover:bg-yellow-100 text-green-900"
                onClick={onLogout}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </header>
      <SettingsModal
        open={showSettings}
        settings={settings}
        loading={settingsLoading}
        saving={settingsSaving}
        error={settingsError}
        onSave={handleSaveSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
}

export default Header;
